import { useNavigate } from "@tanstack/react-router"

import { humanizeMarketplaceId } from "../../lib/marketplace"
import { byMarketplaceRevenue } from "../../lib/marketplace-revenue-order"
import { useMarketplaceSummaries } from "../../services/use-dashboard"
import { useRates } from "../../services/use-rates"
import { Combobox, type ComboboxOption } from "../ui/combobox"
import { MarketplaceLogo } from "./marketplace-logo"

/** The marketplace-scoped pages this control can move between. Spelled out
 *  rather than widened to `string` so a typo cannot compile and a route rename
 *  breaks here first. */
type MarketplaceRoute =
  | "/campaigns/$marketplace"
  | "/pnl/$marketplace"
  | "/storage-refresh/$marketplace"

/**
 * Swaps a marketplace-scoped page between marketplaces — `/pnl/decathlon-cz` ⇄
 * `/pnl/shop-apotheke-it`, and the same for the campaign table and the storage
 * refresh.
 *
 * A dropdown rather than the segmented control the keyword tracker uses: that
 * one switches between two or three observable marketplaces, while every
 * CONFIGURED marketplace has these pages — twelve today — and twelve segments
 * would be wider than the page. Which ones exist is the API's answer, so a new
 * marketplace config appears here without a code change.
 *
 * Ordered biggest-revenue first (`byMarketplaceRevenue`), not alphabetically:
 * the channels an operator opens most are the ones carrying the most money, and
 * a twelve-item list is long enough that where an option sits matters.
 *
 * It lists EVERY configured marketplace, including ones the destination page
 * cannot serve — whether a shop supports a storage refresh takes a read per
 * marketplace, so filtering here would cost twelve requests to render a
 * dropdown. The page states its own unsupported case instead, which is also
 * the honest answer: the marketplace exists, this capability does not.
 *
 * Renders nothing when only one is configured — a control with a single option
 * cannot change anything.
 */
export function MarketplaceSwitch({
  marketplace,
  to,
}: {
  marketplace: string
  to: MarketplaceRoute
}) {
  const summaries = useMarketplaceSummaries()
  const rates = useRates()
  const navigate = useNavigate()
  const options: ComboboxOption[] = [...(summaries.data ?? [])]
    .sort(byMarketplaceRevenue(rates.data))
    .map((summary) => ({
      value: summary.marketplace,
      label: humanizeMarketplaceId(summary.marketplace),
      icon: <MarketplaceLogo marketplace={summary.marketplace} />,
    }))

  if (options.length < 2) {
    return null
  }

  return (
    <Combobox
      options={options}
      value={marketplace}
      aria-label="Marketplace"
      className="w-56"
      searchPlaceholder="Search marketplaces…"
      emptyMessage="No marketplaces"
      onChange={(next) => {
        if (next !== undefined && next !== marketplace) {
          void navigate({ to, params: { marketplace: next } })
        }
      }}
    />
  )
}
